import { RootState } from '../../../app/store';
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import TonnageModel from '../models/TonnageModel';
import TonnageQueryModel from '../models/TonnageQueryModel';
import TonnageNameModel from '../models/TonnageNameModel';
import TonnageOperatorHistoryModel from '../models/TonnageOperatorHistory';

interface tonnageInputState {
    tonnageInput: TonnageInputModel,
    tonnageNames: TonnageNameModel[],
    tonnageSearchResults: TonnageModel[],
    tonnageNameSearchResults: TonnageNameModel[],
    tonnageNameSearchKeyword: string,
    tonnageOperatorHistoryModalOpen: boolean,
    tonnageOperatorHistories: TonnageOperatorHistoryModel[],
    selectedTonnageId: string,
    tonnageQuickInputExpand: boolean,
    tonnageAdvanceInputExpand: boolean,
    triggerFocusTonnageNameInput: number,
    triggerSave: number,
    triggerRefreshInput: number
}

export interface TonnageInputModel {
    id: string,
    headOwner: string,
    operator: string,
    vesselName: string,
    dwt: number | null,
    yearBuilt: number | null,
    portOpen: number,
    dateOpenStart: string,
    dateOpenEnd: string,
    remarks: string,
    gear: number | null,
    draft: number | null,
    loa: number | null,
    class: string,
    beam: number | null,
    grabs: boolean,
    gearless: boolean,
    fullTc: string,
    deck: string,
    capacity: number | null,
    updateFullTc: boolean
}

const initialState: tonnageInputState = {
    tonnageInput: {
        id: "",
        headOwner: "",
        operator: "",
        vesselName: "",
        dwt: null,
        yearBuilt: null,
        portOpen: 0,
        dateOpenStart: "",
        dateOpenEnd: "",
        remarks: "",
        gear: null,
        gearless: false,
        draft: null,
        loa: null,
        class: "",
        beam: null,
        grabs: false,
        fullTc: "",
        deck: "",
        capacity: null,
        updateFullTc: false
    } as TonnageInputModel,
    tonnageNames: [],
    tonnageSearchResults: [],
    tonnageNameSearchResults: [],
    tonnageNameSearchKeyword: "",
    tonnageOperatorHistoryModalOpen: false,
    tonnageOperatorHistories: [],
    selectedTonnageId: "",
    tonnageQuickInputExpand: true,
    tonnageAdvanceInputExpand: true,
    triggerFocusTonnageNameInput: 0,
    triggerSave: 0,
    triggerRefreshInput: 0
}

export const TonnageSlice = createSlice({
    name: "TonnageInput",
    initialState,
    reducers: {
        setTonnageNames: (state, action: PayloadAction<TonnageNameModel[]>) => {
            state.tonnageNames = action.payload;
        },
        addTonnageName: (state, action: PayloadAction<TonnageNameModel>) => {
            state.tonnageNames = [...state.tonnageNames, action.payload];
        },
        setTonnageSearchResults: (state, action: PayloadAction<TonnageModel[]>) => {
            state.tonnageSearchResults = action.payload;
        },
        setTonnageNameSearchResults: (state, action: PayloadAction<TonnageNameModel[]>) => {
            state.tonnageNameSearchResults = action.payload;
        },
        setTonnageNameSearchKeyword: (state, action: PayloadAction<string>) => {
            state.tonnageNameSearchKeyword = action.payload;
        },
        resetTonnageInput: (state) => {
            state.tonnageInput = initialState.tonnageInput;
        },
        setTonnageInput: (state, action: PayloadAction<TonnageInputModel>) => {
            state.tonnageInput = action.payload;
        },
        setTonnageInputId: (state, action: PayloadAction<string>) => {
            state.tonnageInput.id = action.payload;
        },
        setHeadOwner: (state, action: PayloadAction<string>) => {
            state.tonnageInput.headOwner = action.payload;
        },
        setOperator: (state, action: PayloadAction<string>) => {
            state.tonnageInput.operator = action.payload;
        },
        setVesselName: (state, action: PayloadAction<string>) => {
            state.tonnageInput.vesselName = action.payload;
        },
        setDwt: (state, action: PayloadAction<number | null>) => {
            state.tonnageInput.dwt = action.payload;
        },
        setYearBuilt: (state, action: PayloadAction<number | null>) => {
            state.tonnageInput.yearBuilt = action.payload;
        },
        setPortOpen: (state, action: PayloadAction<number>) => {
            state.tonnageInput.portOpen = action.payload;
        },
        setDateOpenStart: (state, action: PayloadAction<string>) => {
            state.tonnageInput.dateOpenStart = action.payload;
        },
        setDateOpenEnd: (state, action: PayloadAction<string>) => {
            state.tonnageInput.dateOpenEnd = action.payload;
        },
        setRemarks: (state, action: PayloadAction<string>) => {
            state.tonnageInput.remarks = action.payload;
        },
        setGear: (state, action: PayloadAction<number | null>) => {
            state.tonnageInput.gear = action.payload;
        },
        setDraft: (state, action: PayloadAction<number | null>) => {
            state.tonnageInput.draft = action.payload;
        },
        setLoa: (state, action: PayloadAction<number | null>) => {
            state.tonnageInput.loa = action.payload;
        },
        setClass: (state, action: PayloadAction<string>) => {
            state.tonnageInput.class = action.payload;
        },
        setBeam: (state, action: PayloadAction<number | null>) => {
            state.tonnageInput.beam = action.payload;
        },
        setGrabs: (state, action: PayloadAction<boolean>) => {
            state.tonnageInput.grabs = action.payload;
        },
        setGearless: (state, action: PayloadAction<boolean>) => {
            state.tonnageInput.gearless = action.payload;
        },
        setFullTc: (state, action: PayloadAction<string>) => {
            state.tonnageInput.fullTc = action.payload;
        },
        setDeck: (state, action: PayloadAction<string>) => {
            state.tonnageInput.deck = action.payload;
        },
        setCapacity: (state, action: PayloadAction<number | null>) => {
            state.tonnageInput.capacity = action.payload;
        },
        setUpdateFullTc: (state, action: PayloadAction<boolean>) => {
            state.tonnageInput.updateFullTc = action.payload;
        },
        setTonnageOperatorHistoryModalOpen: (state, action: PayloadAction<boolean>) => {
            state.tonnageOperatorHistoryModalOpen = action.payload;
        },
        setTonnageHistories: (state, action: PayloadAction<TonnageOperatorHistoryModel[]>) => {
            state.tonnageOperatorHistories = action.payload;
        },
        setSelectedTonnageId: (state, action: PayloadAction<string>) => {
            state.selectedTonnageId = action.payload;
        },
        setTonnageQuickInputExpand: (state, action: PayloadAction<boolean>) => {
            state.tonnageQuickInputExpand = action.payload;
        },
        setTonnageAdvanceInputExpand: (state, action: PayloadAction<boolean>) => {
            state.tonnageAdvanceInputExpand = action.payload;
        },
        setTriggerFocusTonnageNameInput: (state, action: PayloadAction<number>) => {
            state.triggerFocusTonnageNameInput = action.payload;
        },
        setTriggerSave: (state, action: PayloadAction<number>) => {
            state.triggerSave = action.payload;
        },
        setTriggerRefreshInput: (state, action: PayloadAction<number>) => {
            state.triggerRefreshInput = action.payload;
        },
    }
});

export const tonnageInputSelector = createSelector(
    (state: RootState) => state.tonnage,
    (data) => data
);

export const {
    setTonnageNames,
    addTonnageName,
    setTonnageSearchResults,
    setTonnageNameSearchResults,
    setTonnageNameSearchKeyword,
    resetTonnageInput,
    setTonnageInput,
    setTonnageInputId,
    setHeadOwner,
    setOperator,
    setVesselName,
    setDwt,
    setYearBuilt,
    setPortOpen,
    setDateOpenStart,
    setDateOpenEnd,
    setRemarks,
    setGear,
    setDraft,
    setLoa,
    setClass,
    setBeam,
    setGrabs,
    setGearless,
    setFullTc,
    setDeck,
    setCapacity,
    setUpdateFullTc,
    setTonnageOperatorHistoryModalOpen,
    setTonnageHistories,
    setSelectedTonnageId,
    setTonnageQuickInputExpand,
    setTonnageAdvanceInputExpand,
    setTriggerFocusTonnageNameInput,
    setTriggerSave,
    setTriggerRefreshInput
} = TonnageSlice.actions;

export default TonnageSlice.reducer;